import Text from "../../UI/Text";
import { useToast } from "../../UI/Toast/useToast";
import type { Tenant } from "../../../models/tenant";
import type { BulkResult } from "./types";
import bulkStyles from "./BulkActions.module.css";

interface BulkResultCounts {
  successCount: number;
  failureCount: number;
  total: number;
}

interface BulkResultsToastLabels {
  successSection: string;
  failureSection: string;
  successToastTitle: string;
  errorToastTitle: string;
}

/**
 * Toast récapitulatif d'une action en masse sur des locataires : liste les
 * succès et les échecs (avec leur raison) par nom de locataire, et choisit
 * toast.success ou toast.error selon qu'au moins un locataire a été traité.
 * Les libellés (accordés selon les comptes) sont fournis par l'appelant.
 */
export function useBulkResultsToast(tenantsById: Record<number, Tenant>) {
  const toast = useToast();

  return function notifyBulkResults(
    results: BulkResult[],
    buildLabels: (counts: BulkResultCounts) => BulkResultsToastLabels,
  ) {
    const successes = results.filter((r) => r.success);
    const failures = results.filter((r) => !r.success);
    const labels = buildLabels({
      successCount: successes.length,
      failureCount: failures.length,
      total: results.length,
    });
    const tenantName = (tenantId: number) =>
      tenantsById[tenantId]?.nom ?? `Locataire ${tenantId}`;

    const description = (
      <div className={bulkStyles.toastDescription}>
        {successes.length > 0 && (
          <div className={bulkStyles.toastSection}>
            <Text weight="bold">{labels.successSection}</Text>
            <ul className={bulkStyles.toastList}>
              {successes.map((r) => (
                <li key={r.tenant_id}>
                  <Text as="span" variant="deci">
                    {tenantName(r.tenant_id)}
                  </Text>
                </li>
              ))}
            </ul>
          </div>
        )}
        {failures.length > 0 && (
          <div className={bulkStyles.toastSection}>
            <Text weight="bold">{labels.failureSection}</Text>
            <ul className={bulkStyles.toastList}>
              {failures.map((r) => (
                <li key={r.tenant_id}>
                  <Text as="span" variant="deci">
                    <Text as="span" variant="deci" weight="bold">
                      {tenantName(r.tenant_id)}
                    </Text>{" "}
                    : {r.error}
                  </Text>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    );

    if (successes.length === 0) {
      toast.error({ title: labels.errorToastTitle, description });
    } else {
      toast.success({ title: labels.successToastTitle, description });
    }
  };
}
